import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { useAuth } from "../lib/auth";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import PasswordInput from "./components/PasswordInput";
import OpeningHoursDesigner from "./components/OpeningHoursDesigner";
import { User, Bank, IdentificationCard, Key, FloppyDisk, Clock } from "@phosphor-icons/react";
import { toast } from "sonner";
import { SettingsExtensions } from "../components/SettingsExtensions";
import { GestorMultiOrgCards } from "../components/GestorMultiOrgCards";

/**
 * Ajustes del Colaborador — perfil, datos fiscales/bancarios y cambio de contraseña.
 * Cualquier rol puede usar esta página; el Admin tiene su propio Settings con
 * configuración de la organización entera (esto es solo "Mi perfil").
 */
export default function ColaboradorSettings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({
    name: "", avatar_url: "",
    billing_nif: "", billing_address: "", bank_iban: "",
    schedule_per_day: null,
  });
  const [busy, setBusy] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [pwdForm, setPwdForm] = useState({ current: "", next: "", confirm: "" });
  const [pwdBusy, setPwdBusy] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm({
      name: user.name || "",
      avatar_url: user.avatar_url || "",
      billing_nif: user.billing_nif || "",
      billing_address: user.billing_address || "",
      bank_iban: user.bank_iban || "",
      schedule_per_day: user.schedule_per_day || null,
    });
  }, [user]);

  const onAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 512) { toast.error("Avatar > 512 KB"); return; }
    const reader = new FileReader();
    reader.onload = () => setForm((f) => ({ ...f, avatar_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/users/me", form);
      toast.success("Perfil actualizado");
      refresh?.();
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const changePassword = async () => {
    if (pwdForm.next !== pwdForm.confirm) { toast.error("Las contraseñas no coinciden"); return; }
    if (pwdForm.next.length < 6) { toast.error("Mínimo 6 caracteres"); return; }
    setPwdBusy(true);
    try {
      await api.put("/users/me/password", { current_password: pwdForm.current, new_password: pwdForm.next });
      toast.success("Contraseña actualizada");
      setPwdOpen(false);
      setPwdForm({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    finally { setPwdBusy(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl">
      <header>
        <div className="text-xs uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">Mi cuenta</div>
        <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tighter mt-1">Ajustes</h1>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">Perfil, datos fiscales para facturación al titular, IBAN para cobros y seguridad de tu cuenta.</p>
      </header>

      {/* Perfil */}
      <section className="ag-card p-5" data-testid="colab-settings-profile">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">
          <User size={12} weight="fill" /> Perfil
        </div>
        <div className="mt-4 grid grid-cols-1 sm:grid-cols-[120px_1fr] gap-5 items-start">
          <div className="space-y-2">
            <div className="w-28 h-28 rounded-full overflow-hidden border-2 border-[var(--ag-border)] bg-[var(--ag-midnight-soft)] grid place-items-center mx-auto">
              {form.avatar_url ? <img src={form.avatar_url} alt="Avatar" className="w-full h-full object-cover" /> :
                <User size={42} weight="duotone" className="text-[var(--ag-text-dim)]" />}
            </div>
            <label className="block">
              <span className="sr-only">Cambiar avatar</span>
              <Input type="file" accept="image/*" onChange={onAvatar} className="text-[10px]" data-testid="colab-settings-avatar" />
            </label>
          </div>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Nombre completo</Label>
              <Input value={form.name} onChange={(e)=>setForm({...form, name: e.target.value})} className="mt-1 rounded-sm" data-testid="colab-settings-name" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Email</Label>
              <Input value={user?.email || ""} disabled className="mt-1 rounded-sm opacity-60" />
              <p className="text-[10px] text-[var(--ag-text-dim)] mt-1">El email no se puede cambiar — contacta con el titular si necesitas modificarlo.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Datos fiscales */}
      <section className="ag-card p-5" data-testid="colab-settings-fiscal">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">
          <IdentificationCard size={12} weight="fill" /> Datos fiscales · para tu factura al titular
        </div>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">Necesarios cuando generes tu <b>factura mensual</b> al titular. Quedan privados, solo se imprimen en tu PDF.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
          <div>
            <Label className="text-xs uppercase tracking-wider">NIF / DNI</Label>
            <Input value={form.billing_nif} onChange={(e)=>setForm({...form, billing_nif: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" placeholder="12345678A" data-testid="colab-settings-nif" />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">Dirección fiscal</Label>
            <Input value={form.billing_address} onChange={(e)=>setForm({...form, billing_address: e.target.value})} className="mt-1 rounded-sm" placeholder="Calle, número, ciudad" data-testid="colab-settings-address" />
          </div>
        </div>
      </section>

      {/* Datos bancarios */}
      <section className="ag-card p-5" data-testid="colab-settings-bank">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">
          <Bank size={12} weight="fill" /> Cuenta bancaria · para cobrar tu liquidación
        </div>
        <div className="mt-3">
          <Label className="text-xs uppercase tracking-wider">IBAN</Label>
          <Input value={form.bank_iban} onChange={(e)=>setForm({...form, bank_iban: e.target.value.toUpperCase()})} className="mt-1 rounded-sm font-mono-tab" placeholder="ES00 0000 0000 0000 0000 0000" data-testid="colab-settings-iban" />
          <p className="text-[10px] text-[var(--ag-text-dim)] mt-1">El IBAN aparecerá en tu factura para que el titular sepa dónde transferir.</p>
        </div>
      </section>

      {/* Mi horario · Sprint H · Batch 9.1 — Cada usuario puede configurar su propio horario */}
      <section className="ag-card p-5" data-testid="colab-settings-schedule">
        <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[var(--ag-gold)]">
          <Clock size={12} weight="fill" /> Mi horario · disponibilidad para reservas
        </div>
        <p className="text-sm text-[var(--ag-text-dim)] mt-2">
          Define cuándo estás disponible cada día. El sistema de reservas públicas y la agenda interna
          respetarán este horario al asignarte citas. Si lo dejas vacío, se usa el horario general del centro.
        </p>
        <div className="mt-4">
          <OpeningHoursDesigner
            value={form.schedule_per_day}
            onChange={(next)=>setForm((f)=>({ ...f, schedule_per_day: next }))}
          />
        </div>
      </section>

      <div className="flex gap-3 flex-wrap">
        <Button onClick={save} disabled={busy} className="ag-btn-gold h-11 px-6" data-testid="colab-settings-save">
          <FloppyDisk size={15} weight="fill" className="mr-1.5" /> {busy ? "Guardando…" : "Guardar cambios"}
        </Button>
        <Button onClick={()=>setPwdOpen(true)} variant="outline" className="h-11 rounded-sm" data-testid="colab-settings-password">
          <Key size={15} className="mr-1.5" /> Cambiar contraseña
        </Button>
      </div>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent className="rounded-sm max-w-sm" data-testid="colab-pwd-dialog">
          <DialogHeader><DialogTitle className="font-display text-2xl tracking-tighter">Cambiar contraseña</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Contraseña actual</Label>
              <PasswordInput value={pwdForm.current} onChange={(e)=>setPwdForm({...pwdForm, current: e.target.value})} testId="pwd-current" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Nueva contraseña</Label>
              <PasswordInput value={pwdForm.next} onChange={(e)=>setPwdForm({...pwdForm, next: e.target.value})} testId="pwd-new" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Repite nueva contraseña</Label>
              <PasswordInput value={pwdForm.confirm} onChange={(e)=>setPwdForm({...pwdForm, confirm: e.target.value})} testId="pwd-confirm" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPwdOpen(false)} className="rounded-sm">Cancelar</Button>
            <Button onClick={changePassword} disabled={pwdBusy} className="ag-btn-gold" data-testid="pwd-submit">
              {pwdBusy ? "Cambiando…" : "Confirmar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <GestorMultiOrgCards role={user?.role} />

      <SettingsExtensions role={user?.role} />
    </div>
  );
}
